import React, { useState } from "react";
import styled from "@emotion/styled";

import { Button } from "../Button";
import { CardFooter } from "../Card/Card";
import { CONTACT_TYPES_TEXT, TEST_ID_PREFIX } from "./constants";
import type { Contact, ContactCardProps } from "./types";
import { AddNewContact } from "./AddNewContact";
import { ContactInfoModal } from "./ContactInfoModal";
import { ContactOption } from "./ContactOption";

const ContactListEdit = ({
  contactType,
  contactList,
  selectedContactId,
  disabled,
  customApprovedStatusText,
  customNotApprovedStatusText,
  customNotCheckedStatusText,
  saveContact,
  updateSelectedId,
  onCancel,
  disableEditEmail,
  customAddNewText,
  secondaryButtonText,
}: ContactListEditProps) => {
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact>();
  const [newContactId, setNewContactId] = useState(selectedContactId);

  const isSelectedContact = (contactOption: Contact) =>
    contactOption.contactId === newContactId;

  const openModal = (contact?: Contact) => {
    setEditingContact(contact);
    setShowModal(true);
    return contact;
  };

  const closeModal = () => {
    setEditingContact(undefined);
    setShowModal(false);
  };

  const handleSelected = (selectedContact: Contact) => {
    setNewContactId(selectedContact.contactId);
  };

  const handleSave = async (updatedContact: Contact) => {
    const newContact = await saveContact(updatedContact);

    setNewContactId(newContact.contactId);
  };

  const isSelectionValid = () => {
    const foundContact = contactList.find(
      (contact) => contact.contactId === newContactId
    );
    const isContactInfoComplete = [
      "customerEmail",
      "customerPrimaryPhone",
      "customerFirstName",
      "customerLastName",
    ].every((property) => foundContact?.[property]);

    return selectedContactId !== newContactId && isContactInfoComplete;
  };

  return (
    <>
      <ContactListContainer>
        {contactList?.map((contact) => (
          <ContactOption
            key={contact.contactId}
            contactType={contactType}
            contact={contact}
            isSelected={isSelectedContact(contact)}
            onSelect={handleSelected}
            onEdit={openModal}
            customApprovedStatusText={customApprovedStatusText}
            customNotApprovedStatusText={customNotApprovedStatusText}
            customNotCheckedStatusText={customNotCheckedStatusText}
          />
        ))}
        <AddNewContact
          contactType={contactType}
          onAdd={openModal}
          customAddNewText={customAddNewText}
        />
      </ContactListContainer>
      <ContactInfoModal
        isOpen={showModal}
        contactType={contactType}
        contact={editingContact}
        loading={disabled}
        onSave={handleSave}
        onClose={closeModal}
        disableEditEmail={disableEditEmail}
        customAddNewText={customAddNewText}
      />
      <CardFooter
        rightContent={
          <>
            <Button
              size="sm"
              color="secondary"
              onClick={onCancel}
              disabled={disabled}
              data-testid={`${TEST_ID_PREFIX[contactType]}-cancel`}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!isSelectionValid() || disabled}
              onClick={() => updateSelectedId(newContactId)}
              data-testid={`${TEST_ID_PREFIX[contactType]}-update`}
            >
              {secondaryButtonText ??
                `Update ${CONTACT_TYPES_TEXT[contactType]}`}
            </Button>
          </>
        }
      />
    </>
  );
};

interface ContactListEditProps extends ContactCardProps {
  selectedContactId?: string;
  onCancel: () => void;
  disableEditEmail?: boolean;
  saveContact: (contact: Contact) => Promise<Contact>;
  updateSelectedId: (id: string) => void;
}

const ContactListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export { ContactListEdit };
